/**
 * Commerces & services de proximité — OpenStreetMap via Overpass.
 *
 * Source : public Overpass instance (overpass-api.de). CORS ✅, anonyme,
 * rate-limit conviviale mais pas infinie → d'où le cache Firestore 30 j.
 *
 * Catégories retenues :
 *   - alimentation  — commerces de bouche (boulangerie, supermarché…)
 *   - restauration  — resto, café, bar, fast-food
 *   - services      — pharmacie, poste, banque, ATM, coiffeur, fleuriste…
 *   - santé         — médecins, dentistes, cliniques
 *
 * On volontairement ignore les `shop=yes` ou types exotiques (tatoueur,
 * toiletteur chien, etc.) — le but est un profil quartier, pas un
 * inventaire OSM exhaustif.
 *
 * Radius — piloté par le tier de la commune (`localityTier.ts`) :
 *   rural       8 km   — boulangerie à 3–5 km fréquente en commune < 2 k
 *   petite      5 km   — 2–10 k hab
 *   moyenne     2 km   — 10–50 k hab
 *   grande      1 km   — 50–200 k hab
 *   métropole   700 m  — > 200 k hab + arrondissements Paris/Lyon/Marseille
 *
 * En urbain dense, Overpass renvoie plusieurs milliers d'éléments même au
 * rayon réduit mais on post-trie par distance et on cap à 80 items au total
 * dans le doc Firestore, donc la taille sérialisée reste sous 15 KB.
 */

import { useMemo } from "react";
import { coordKey, haversineMeters, useEnvBlock } from "./shared";
import { useCommuneInfo, type LocalityTier } from "./localityTier";
import type { GeocodeResult } from "../types";

// Radius per locality tier, in meters.
export const COMMERCES_RADIUS_BY_TIER: Record<LocalityTier, number> = {
  rural: 8_000,
  petite: 5_000,
  moyenne: 2_000,
  grande: 1_000,
  metropole: 700,
};

const COMMERCES_TTL_DAYS = 30;

// Overpass timeout hint (seconds, written into the query). Default is 25s
// but dense urban centers with this radius can need longer — we bump to
// 60s and add 5s on the fetch timeout to give the server room. If the
// primary mirror can't serve it, the kumi fallback kicks in.
const OVERPASS_TIMEOUT_S = 60;

// Two public mirrors — we try the primary first, fall back if it 429s.
// Both are maintained by the OpenStreetMap community; no API key needed.
// NB: overpass.openstreetmap.fr (OSM-FR) rejects cross-origin calls from
// non-allowlisted domains (403 + missing CORS header) — not usable client-side.
const OVERPASS_ENDPOINTS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
];

export type CommerceCategory =
  | "alimentation"
  | "restauration"
  | "services"
  | "sante";

export type CommerceItem = {
  id: string; // "node/123" or "way/456"
  name: string;
  category: CommerceCategory;
  // OSM tag value, e.g. "bakery", "restaurant", "pharmacy".
  subtype: string;
  lat: number;
  lng: number;
  distance_m: number;
};

export type CommercesData = {
  center: { lat: number; lng: number };
  radius_m: number;
  tier: LocalityTier;
  fetchedAt: string;
  // Totals per category — cheap to display as a bandeau without iterating
  // the full list.
  counts: Record<CommerceCategory, number>;
  // Capped to the 80 nearest to keep the Firestore doc small. OSM can easily
  // return 300+ elements in dense Parisian arrondissements.
  items: CommerceItem[];
};

// ─── Tag → category mapping ─────────────────────────────────────────────

// `shop=*` values we care about, keyed by resulting category.
const SHOP_CATEGORIES: Record<string, CommerceCategory> = {
  bakery: "alimentation",
  pastry: "alimentation",
  supermarket: "alimentation",
  convenience: "alimentation",
  butcher: "alimentation",
  greengrocer: "alimentation",
  cheese: "alimentation",
  fishmonger: "alimentation",
  seafood: "alimentation",
  wine: "alimentation",
  alcohol: "alimentation",
  beverages: "alimentation",
  deli: "alimentation",
  chocolate: "alimentation",
  coffee: "alimentation",
  tea: "alimentation",
  general: "alimentation",
  health_food: "alimentation",
  organic: "alimentation",
  farm: "alimentation",
  // services
  hairdresser: "services",
  beauty: "services",
  florist: "services",
  optician: "services",
  newsagent: "services",
  books: "services",
  stationery: "services",
  laundry: "services",
  dry_cleaning: "services",
  shoe_repair: "services",
  tailor: "services",
  copyshop: "services",
  // santé (rare in `shop=` but exists)
  medical_supply: "sante",
};

// `amenity=*` mapping.
const AMENITY_CATEGORIES: Record<string, CommerceCategory> = {
  restaurant: "restauration",
  cafe: "restauration",
  bar: "restauration",
  pub: "restauration",
  fast_food: "restauration",
  food_court: "restauration",
  biergarten: "restauration",
  ice_cream: "restauration",
  pharmacy: "sante",
  doctors: "sante",
  dentist: "sante",
  clinic: "sante",
  veterinary: "sante",
  post_office: "services",
  post_box: "services",
  bank: "services",
  atm: "services",
  bureau_de_change: "services",
};

// ─── Fetch ──────────────────────────────────────────────────────────────

export async function fetchCommerces(
  lat: number,
  lng: number,
  radiusM: number,
  tier: LocalityTier,
): Promise<CommercesData> {
  const shopKeys = Object.keys(SHOP_CATEGORIES).join("|");
  const amenityKeys = Object.keys(AMENITY_CATEGORIES).join("|");

  // `nwr(around:R,LAT,LNG)[tag~"^(a|b|c)$"]` — Overpass selects nodes, ways,
  // and relations within the radius whose tag matches the regex. `out center`
  // gives us a point even for ways/relations (useful for supermarkets mapped
  // as buildings).
  const q = `[out:json][timeout:${OVERPASS_TIMEOUT_S}];
(
  nwr(around:${radiusM},${lat},${lng})["shop"~"^(${shopKeys})$"];
  nwr(around:${radiusM},${lat},${lng})["amenity"~"^(${amenityKeys})$"];
);
out center tags;`;

  const body = new URLSearchParams({ data: q });

  let payload: OverpassResponse | null = null;
  let lastErr: unknown = null;
  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
        // Client-side ceiling slightly above the Overpass server-side hint,
        // so we abort cleanly instead of spinning forever if the mirror is
        // wedged. The mirror fallback loop retries on the next endpoint.
        signal: AbortSignal.timeout((OVERPASS_TIMEOUT_S + 5) * 1000),
      });
      if (!res.ok) {
        // 429 / 504 are routine on the public mirror — try the next one.
        lastErr = new Error(`overpass HTTP ${res.status}`);
        continue;
      }
      payload = (await res.json()) as OverpassResponse;
      break;
    } catch (e) {
      lastErr = e;
    }
  }
  if (!payload) {
    throw lastErr instanceof Error ? lastErr : new Error("overpass unreachable");
  }

  const rawItems: CommerceItem[] = [];
  for (const el of payload.elements ?? []) {
    const pt = pointOf(el);
    if (!pt) continue;
    const tags = el.tags ?? {};
    const category = categorize(tags);
    if (!category) continue;
    const name = tags.name?.trim();
    // Items without a name are rare for the tag set we selected, and they
    // tend to be drafts — skip to keep the UI noise-free.
    if (!name) continue;
    rawItems.push({
      id: `${el.type}/${el.id}`,
      name,
      category,
      subtype: pickSubtype(tags),
      lat: pt.lat,
      lng: pt.lng,
      distance_m: Math.round(haversineMeters(lat, lng, pt.lat, pt.lng)),
    });
  }

  // Deduplicate on (name, subtype) within 25 m — OSM often has the same
  // shop mapped as a node + a way, and we don't want the card to show the
  // same bakery twice.
  const deduped = dedupeNearby(rawItems);

  deduped.sort((a, b) => a.distance_m - b.distance_m);
  const items = deduped.slice(0, 80);

  const counts: Record<CommerceCategory, number> = {
    alimentation: 0,
    restauration: 0,
    services: 0,
    sante: 0,
  };
  for (const it of deduped) counts[it.category] += 1;

  return {
    center: { lat, lng },
    radius_m: radiusM,
    tier,
    fetchedAt: new Date().toISOString(),
    counts,
    items,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────

type OverpassElement = {
  type: "node" | "way" | "relation";
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
};
type OverpassResponse = { elements?: OverpassElement[] };

function pointOf(el: OverpassElement): { lat: number; lng: number } | null {
  if (typeof el.lat === "number" && typeof el.lon === "number") {
    return { lat: el.lat, lng: el.lon };
  }
  if (el.center) return { lat: el.center.lat, lng: el.center.lon };
  return null;
}

function categorize(tags: Record<string, string>): CommerceCategory | null {
  const shop = tags.shop;
  if (shop) {
    const c = SHOP_CATEGORIES[shop];
    if (c) return c;
  }
  const amenity = tags.amenity;
  if (amenity) {
    const c = AMENITY_CATEGORIES[amenity];
    if (c) return c;
  }
  // Healthcare key (OSM uses both amenity=doctors and healthcare=doctor)
  const healthcare = tags.healthcare;
  if (healthcare === "doctor" || healthcare === "dentist" || healthcare === "clinic") {
    return "sante";
  }
  return null;
}

function pickSubtype(tags: Record<string, string>): string {
  return (
    tags.shop ??
    tags.amenity ??
    tags.healthcare ??
    "autre"
  );
}

/**
 * Drop obvious duplicates: same (name, subtype) within 25 m. OSM commonly
 * carries a shop as both a node and the enclosing building way — Overpass
 * returns both, and we don't care about the distinction for a neighbourhood
 * profile.
 */
function dedupeNearby(items: CommerceItem[]): CommerceItem[] {
  const out: CommerceItem[] = [];
  for (const it of items) {
    const dup = out.find(
      (o) =>
        o.name.toLowerCase() === it.name.toLowerCase() &&
        o.subtype === it.subtype &&
        haversineMeters(o.lat, o.lng, it.lat, it.lng) < 25,
    );
    if (!dup) out.push(it);
  }
  return out;
}

// ─── Hook ───────────────────────────────────────────────────────────────

/**
 * `useCommerces` — cache-first loader bound to a fiche + geocoded point.
 * Waits for the commune's locality tier before scheduling any fetch, so
 * we don't pay an Overpass round-trip at a stale default radius.
 */
export function useCommerces(
  ficheId: string | null,
  geocode: GeocodeResult | null,
) {
  const commune = useCommuneInfo(geocode?.insee ?? null);

  const opts = useMemo(() => {
    if (!ficheId || !geocode || !commune) return null;
    const radius = COMMERCES_RADIUS_BY_TIER[commune.tier];
    return {
      ficheId,
      blockId: "commerces" as const,
      inputsKey: `v2|${coordKey(geocode.lat, geocode.lng)}|r${radius}|t${commune.tier}`,
      ttlDays: COMMERCES_TTL_DAYS,
      fetcher: () =>
        fetchCommerces(geocode.lat, geocode.lng, radius, commune.tier),
    };
  }, [ficheId, geocode, commune]);

  return useEnvBlock<CommercesData>(opts);
}
